/**
 * <s-lazy-video> — Muted, looping, inline video tile that only downloads when it scrolls
 * near the viewport, with a pause toggle and a sound toggle.
 *
 * Markup contract (written by blocks/lazy-video.liquid):
 *   <s-lazy-video data-state="paused" data-audio="off">
 *     <video muted loop playsinline preload="none" poster="..." data-src="..."></video>
 *     <button type="button" data-toggle aria-pressed="false">...</button>
 *     <button type="button" data-sound aria-pressed="false">...</button>
 *   </s-lazy-video>
 *
 * Behaviour:
 * - The source is attached the first time the tile comes within 200px of the viewport.
 * - It plays (muted) while visible and pauses when scrolled away.
 * - Reduced-motion visitors get the poster and a play button, never autoplay.
 * - The pause toggle lets anyone pause or resume; a manual pause is respected on return.
 * - Sound: browsers only allow sound after a user gesture, so videos always start muted. Tapping the
 *   speaker button, or the video itself, unmutes this tile inside that tap (works on iPhone Safari and
 *   Android Chrome with playsinline), shows the speaker-on icon, pauses the surrounding <s-marquee>
 *   (data-paused) and tells every other tile to mute, so only one plays sound at a time. Tapping again
 *   mutes it and lets the row scroll again. Pausing the video or scrolling it off screen also mutes it.
 *
 * State on the host: data-state "paused" | "playing", data-audio "off" | "on" (icon styling).
 */
const SOUND_EVENT = 'lazy-video:sound-on';

class SLazyVideo extends HTMLElement {
  #observer = null;
  #video = null;
  #toggle = null;
  #sound = null;
  #userPaused = false;
  #ac = null;

  connectedCallback() {
    this.#video = this.querySelector('video');
    this.#toggle = this.querySelector('[data-toggle]');
    this.#sound = this.querySelector('[data-sound]');
    if (!this.#video) return;

    this.#ac = new AbortController();
    const signal = this.#ac.signal;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#userPaused = reducedMotion;
    this.#video.muted = true;
    this.#setState('paused');
    this.#setAudio(false);

    this.#toggle?.addEventListener('click', this.#onToggle, { signal });
    this.#sound?.addEventListener('click', this.#onSound, { signal });
    // A tap on the video itself (anywhere except the two buttons) does the same as the sound button.
    this.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-toggle], [data-sound]')) return;
      this.#onSound();
    }, { signal });

    // Only one tile may have sound: mute when another one turns its sound on.
    document.addEventListener(SOUND_EVENT, (event) => {
      if (event.detail !== this && this.#hasSound()) this.#soundOff({ resumeRow: false });
    }, { signal });

    this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '200px 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#ac?.abort();
    this.#ac = null;
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.#attachSource();
        if (!this.#userPaused) this.#play();
      } else {
        if (this.#hasSound()) this.#soundOff({ resumeRow: true });
        this.#pause();
      }
    }
  };

  #onToggle = () => {
    if (this.#video.paused) {
      this.#userPaused = false;
      this.#attachSource();
      this.#play();
    } else {
      this.#userPaused = true;
      if (this.#hasSound()) this.#soundOff({ resumeRow: true });
      this.#pause();
    }
  };

  #onSound = () => {
    if (this.#hasSound()) {
      this.#soundOff({ resumeRow: true });
      return;
    }
    // Everything below runs synchronously inside the tap, which is what lets mobile browsers allow sound.
    document.dispatchEvent(new CustomEvent(SOUND_EVENT, { detail: this }));
    this.#attachSource();
    this.#video.muted = false;
    this.#video.volume = 1;
    this.#userPaused = false;
    this.#play({ onBlocked: () => this.#soundOff({ resumeRow: true }) });
    this.#setAudio(true);
    this.#row()?.setAttribute('data-paused', '');
  };

  #soundOff({ resumeRow }) {
    this.#video.muted = true;
    this.#setAudio(false);
    if (resumeRow) this.#row()?.removeAttribute('data-paused');
  }

  #hasSound() {
    return this.dataset.audio === 'on';
  }

  #row() {
    return this.closest('s-marquee');
  }

  #attachSource() {
    const src = this.#video.dataset.src;
    if (!src || this.#video.getAttribute('src')) return;
    this.#video.src = src;
  }

  #play({ onBlocked } = {}) {
    const attempt = this.#video.play();
    this.#setState('playing');
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        this.#setState('paused');
        onBlocked?.();
      });
    }
  }

  #pause() {
    this.#video.pause();
    this.#setState('paused');
  }

  #setState(state) {
    this.dataset.state = state;
    if (!this.#toggle) return;
    const playing = state === 'playing';
    this.#toggle.setAttribute('aria-pressed', playing ? 'false' : 'true');
    this.#toggle.setAttribute('aria-label', playing ? this.#toggle.dataset.labelPause : this.#toggle.dataset.labelPlay);
  }

  #setAudio(on) {
    this.dataset.audio = on ? 'on' : 'off';
    if (!this.#sound) return;
    this.#sound.setAttribute('aria-pressed', on ? 'true' : 'false');
    this.#sound.setAttribute('aria-label', on ? this.#sound.dataset.labelOff : this.#sound.dataset.labelOn);
  }
}

if (!customElements.get('s-lazy-video')) {
  customElements.define('s-lazy-video', SLazyVideo);
}
